import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Send, Paperclip, Copy, AlertCircle, CheckCircle, Mail, ShieldAlert, Settings, ExternalLink, Save, FileText, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import SenderIdentityNotice from './SenderIdentityNotice';
import './EmailPopup.css';

// Quill toolbar config — kept lightweight, matches typical email formatting needs
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean']
  ]
};
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'color', 'background', 'list', 'align', 'link'];

// Quill needs HTML input. Convert plain-text (templates loaded from older state) to HTML once.
const looksLikeHtml = (s) => typeof s === 'string' && /<\/?[a-z][\s\S]*?>/i.test(s);
const escapeHtml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const plainToHtml = (text) => {
  if (!text) return '';
  if (looksLikeHtml(text)) return text;
  return escapeHtml(text)
    .split('\n')
    .map(line => line.length ? line : '<br>')
    .map(line => `<p>${line}</p>`)
    .join('');
};
// Strip tags to count visible characters and validate "required" / max length against real content
const htmlToPlain = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').replace(/ /g, ' ');
};

// Custom email template slots — module scope so the empty-state factory
// isn't recreated on every render (avoids exhaustive-deps warnings).
const TEMPLATE_SLOTS = 5;
const emptyTemplates = () => Array.from({ length: TEMPLATE_SLOTS }, () => ({ subject: '', body: '' }));

const EmailPopup = ({
  isOpen = false,
  onClose = () => {},
  recipient = {},
  onSend = null,
  templates = [],
  defaultTemplate = 0,
  customSubject = '',
  customMessage = '',
  showTemplateSelection = true,
  allowAttachments = true,
  allowCc = true,
  readOnlyTo = false,
  customTitle = '',
  customStyles = {},
  allEmails = [],
  socialMedia = {},
  validationOptions = {
    maxSubjectLength: 100,
    maxMessageLength: 5000,
    maxAttachmentSize: 10 * 1024 * 1024,
    allowedFileTypes: ['.pdf', '.doc', '.docx', '.txt', '.jpg', '.jpeg', '.png', '.gif'],
    requireSubject: true,
    requireMessage: true
  }
}) => {
  // Guard against null recipient
  recipient = recipient || {};

  const [emailForm, setEmailForm] = useState({
    to: '',
    cc: '',
    subject: '',
    message: '',
    selectedTemplate: defaultTemplate,
    attachments: []
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [senderStatus, setSenderStatus] = useState({ status: 'loading', senderEmail: null, verified: false });
  const [senderEmails, setSenderEmails] = useState([]); // all verified sender emails
  // Company name + postal address, which the send path requires on the account.
  // Assume complete until told otherwise, so a slow or failed check never
  // greets the user with a warning about something that may well be filled in.
  const [identity, setIdentity] = useState({ complete: true, blocking: false, missing: [] });
  const [selectedSenderId, setSelectedSenderId] = useState('primary'); // which sender to use
  const [selectedToEmails, setSelectedToEmails] = useState([]);
  const [activeTab, setActiveTab] = useState('compose'); // 'compose' or 'template'
  const [customTemplates, setCustomTemplates] = useState(emptyTemplates);
  const [editingIndex, setEditingIndex] = useState(0); // which slot the Custom Template tab is editing
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateMessage, setTemplateMessage] = useState('');
  const navigate = useNavigate();

  // Build email options from allEmails prop or recipient email.
  // Flatten any comma/semicolon-separated values nested inside individual items —
  // the contact-finder occasionally returns one entry holding multiple addresses,
  // which used to collapse the multiselect to a single "row" with everything jammed in.
  const emailOptions = (() => {
    const collect = (src) => {
      const out = [];
      const arr = Array.isArray(src) ? src : (src ? [src] : []);
      arr.forEach(item => {
        const raw = (typeof item === 'string' ? item : (item?.value || item?.email || item?.emailAddress || '')).toString();
        raw.split(/[,;]+/).forEach(part => {
          const v = part.trim();
          if (v) out.push(v);
        });
      });
      return [...new Set(out)];
    };
    if (allEmails && allEmails.length > 0) return collect(allEmails);
    const recipientEmail = recipient.email || recipient.value || recipient.emailAddress || '';
    return collect(recipientEmail);
  })();

  // Build social media links from socialMedia prop
  const socialLinks = (() => {
    if (!socialMedia || typeof socialMedia !== 'object') return [];
    const links = [];
    const platformIcons = {
      linkedin: { label: 'LinkedIn', color: 'bg-blue-600' },
      twitter: { label: 'Twitter/X', color: 'bg-black' },
      facebook: { label: 'Facebook', color: 'bg-blue-500' },
      instagram: { label: 'Instagram', color: 'bg-pink-500' },
      youtube: { label: 'YouTube', color: 'bg-red-600' }
    };
    Object.entries(socialMedia).forEach(([platform, urls]) => {
      const urlList = Array.isArray(urls) ? urls : (urls ? [urls] : []);
      urlList.forEach(url => {
        if (url) {
          const info = platformIcons[platform.toLowerCase()] || { label: platform, color: 'bg-gray-600' };
          links.push({ platform: info.label, url, color: info.color });
        }
      });
    });
    return links;
  })();

  const defaultTemplates = useMemo(() => [
    {
      name: "Partnership Opportunity",
      subject: "Partnership Opportunity - Website Affiliate Program",
      message: `Hello [Name],

I hope this email finds you well. My name is [Your Name], and I'm reaching out from [Your Website/Company Name].

I've been following your work and really admire what you've built with [Their Website]. Your content quality and audience engagement are impressive.

I'm writing to explore a potential affiliate partnership opportunity. We're looking to collaborate with quality websites like yours to create mutually beneficial relationships.

Could you please share:
• Your current affiliate program criteria and requirements
• Commission structure and payment terms
• Any specific content or promotional guidelines
• Minimum traffic or performance thresholds

I'd be happy to provide more details about our program and discuss how we can work together effectively.

Looking forward to hearing from you.

Best regards,
[Your Name]
[Your Title]
[Your Contact Information]`
    },
    {
      name: "Collaboration Inquiry",
      subject: "Collaboration Inquiry - Affiliate Partnership",
      message: `Hi [Name],

I hope you're having a great day! I'm [Your Name] from [Your Website/Company Name].

I came across [Their Website] and was impressed by your content and community. Your approach to [specific niche/topic] really resonates with what we're trying to achieve.

We're expanding our affiliate program and would love to explore a partnership with you. Our program offers competitive commissions and excellent support for our partners.

I'd appreciate if you could share:
• Your affiliate partnership requirements
• What commission rates you typically work with
• Your audience demographics and traffic insights
• Any specific promotional preferences or restrictions

We believe there's great potential for a successful collaboration and would love to discuss this further at your convenience.

Thank you for your time, and I look forward to your response.

Warm regards,
[Your Name]
[Your Position]
[Contact Details]`
    },
    {
      name: "Business Inquiry",
      subject: "Business Inquiry - [Your Website Name]",
      message: `Dear [Name],

Greetings from [Your Website/Company Name]! I'm [Your Name], and I handle our partnership development.

I've been researching quality websites in our niche and [Their Website] consistently comes up as a leader in the space. Your content quality and audience engagement metrics are exactly what we look for in potential partners.

We're currently accepting applications for our affiliate program and believe you'd be an excellent fit. Our program features:
• Competitive commission structures
• Dedicated affiliate support
• Marketing materials and resources
• Timely payments and transparent reporting

To move forward, I'd love to learn more about:
• Your current affiliate partnerships and criteria
• Preferred commission models (flat rate, percentage, tiered)
• Content integration preferences
• Any questions about our products/services

Would you be open to a brief call or email exchange to discuss this opportunity in more detail?

Best wishes,
[Your Name]
[Your Role]
[Your Website/Company Name]
[Contact Information]`
    }
  ], []);

  const emailTemplates = useMemo(() => templates.length > 0 ? templates : defaultTemplates, [templates, defaultTemplates]);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateEmail = (email) => {
    if (!email.trim()) return 'Email is required';
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validateSubject = (subject) => {
    if (validationOptions.requireSubject && !subject.trim()) return 'Subject is required';
    if (subject.length > validationOptions.maxSubjectLength) return `Subject must be ${validationOptions.maxSubjectLength} characters or less`;
    return '';
  };

  const validateMessage = (message) => {
    // message is HTML — validate against visible plain-text content, not tag bytes
    const plain = htmlToPlain(message).trim();
    if (validationOptions.requireMessage && !plain) return 'Message is required';
    if (plain.length > validationOptions.maxMessageLength) return `Message must be ${validationOptions.maxMessageLength} characters or less`;
    return '';
  };

  const validateAttachment = (file) => {
    if (file.size > validationOptions.maxAttachmentSize) return `File size must be less than ${(validationOptions.maxAttachmentSize / 1024 / 1024).toFixed(1)}MB`;
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (validationOptions.allowedFileTypes.length > 0 && !validationOptions.allowedFileTypes.includes(fileExtension)) {
      return `File type not allowed. Allowed types: ${validationOptions.allowedFileTypes.join(', ')}`;
    }
    return '';
  };

  const validateForm = (form = emailForm) => {
    const newErrors = {};
    // To field supports comma-separated emails
    const toEmails = form.to.split(',').map(e => e.trim()).filter(Boolean);
    if (toEmails.length === 0) {
      newErrors.to = 'Email is required';
    } else {
      for (const email of toEmails) {
        const toError = validateEmail(email);
        if (toError) { newErrors.to = `Invalid email: ${email}`; break; }
      }
    }
    if (form.cc.trim()) {
      const ccEmails = form.cc.split(',').map(email => email.trim());
      for (const email of ccEmails) {
        const ccError = validateEmail(email);
        if (ccError) { newErrors.cc = `Invalid CC email: ${email}`; break; }
      }
    }
    const subjectError = validateSubject(form.subject);
    if (subjectError) newErrors.subject = subjectError;
    const messageError = validateMessage(form.message);
    if (messageError) newErrors.message = messageError;
    return newErrors;
  };

  const initializedForOpenRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      initializedForOpenRef.current = false;
      return;
    }
    // Only initialize the form once per open session — parent re-renders that
    // change identity of `templates`/`recipient` must not reset user edits.
    if (initializedForOpenRef.current) return;
    initializedForOpenRef.current = true;

    {
      const initialTemplate = emailTemplates[defaultTemplate] || emailTemplates[0];
      // Reuse the same flattening logic as emailOptions so a single comma-jammed
      // entry (e.g. "a@x.com, b@y.com") gets split into individual contacts.
      const flatten = (src) => {
        const out = [];
        const arr = Array.isArray(src) ? src : (src ? [src] : []);
        arr.forEach(item => {
          const raw = (typeof item === 'string' ? item : (item?.value || item?.email || item?.emailAddress || '')).toString();
          raw.split(/[,;]+/).forEach(part => {
            const v = part.trim();
            if (v) out.push(v);
          });
        });
        return [...new Set(out)];
      };
      const allEmailList = allEmails && allEmails.length > 0
        ? flatten(allEmails)
        : flatten(recipient.email || recipient.value || recipient.emailAddress || '');
      // When the finder surfaces multiple contacts, pre-select the first one so the
      // user just has to add others. (Pre-selecting all would auto-spam everyone.)
      const initialSelection = allEmailList.length > 0 ? [allEmailList[0]] : [];
      setSelectedToEmails(initialSelection);
      const defaultTo = initialSelection.join(', ');
      const initialForm = {
        to: defaultTo,
        cc: '',
        subject: customSubject || initialTemplate?.subject || '',
        message: plainToHtml(customMessage || initialTemplate?.message || ''),
        selectedTemplate: defaultTemplate,
        attachments: []
      };
      setEmailForm(initialForm);
      const initialErrors = validateForm(initialForm);
      setErrors(initialErrors);
      setTouched({});
    }
  }, [isOpen, recipient, defaultTemplate, customSubject, customMessage, emailTemplates]);

  useEffect(() => {
    if (!isOpen) {
      setEmailForm({ to: '', cc: '', subject: '', message: '', selectedTemplate: 0, attachments: [] });
      setErrors({});
      setTouched({});
      setSenderStatus({ status: 'loading', senderEmail: null, verified: false });
      setSenderEmails([]);
      setIdentity({ complete: true, blocking: false, missing: [] });
      setSelectedSenderId('primary');
      setSelectedToEmails([]);
      setActiveTab('compose');
      setTemplateMessage('');
      setEditingIndex(0);
    }
  }, [isOpen]);

  // Check sender email verification status + fetch full list when popup opens
  useEffect(() => {
    if (isOpen) {
      const checkSenderStatus = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/senderEmail/check-status`, {
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
          });
          if (response.ok) {
            const data = await response.json();
            setSenderStatus(data);
          } else {
            setSenderStatus({ status: 'error', senderEmail: null, verified: false });
          }
        } catch (err) {
          console.error('Failed to check sender email status:', err);
          setSenderStatus({ status: 'error', senderEmail: null, verified: false });
        }
      };

      const fetchSenderList = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/senderEmail/list`, {
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              const verified = data.data.filter(e => e.is_verified);
              setSenderEmails(verified);
              // default to primary if available, else first verified
              const primary = verified.find(e => e.is_primary);
              setSelectedSenderId(primary ? 'primary' : (verified[0]?.id ?? 'primary'));
            }
          }
        } catch (err) {
          console.error('Failed to fetch sender email list:', err);
        }
      };

      // Company name and postal address. Asked here rather than after the user
      // has written an email, because the send path refuses without them.
      const checkIdentity = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/userprofile/outreach-identity`, {
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
          });
          if (!response.ok) return;
          const data = await response.json();
          if (data.success) {
            setIdentity({
              complete: data.complete !== false,
              blocking: data.blocking === true,
              missing: Array.isArray(data.missing) ? data.missing : []
            });
          }
        } catch (err) {
          // Leave it as complete: the send path is the real gate, and a failed
          // check must not stop somebody who has their details in order.
          console.error('Failed to check sender identity:', err);
        }
      };

      checkSenderStatus();
      fetchSenderList();
      checkIdentity();
    }
  }, [isOpen]);

  // Fetch all 5 custom templates when popup opens
  useEffect(() => {
    if (isOpen) {
      const fetchTemplates = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/userprofile/email-template`, {
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              if (Array.isArray(data.templates) && data.templates.length) {
                // Normalise to exactly TEMPLATE_SLOTS entries
                const normalised = emptyTemplates().map((empty, i) => ({
                  subject: data.templates[i]?.subject || '',
                  body:    data.templates[i]?.body    || ''
                }));
                setCustomTemplates(normalised);
              } else if (data.data) {
                // Back-compat: server returned only the first slot
                setCustomTemplates(prev => {
                  const next = [...prev];
                  next[0] = { subject: data.data.subject || '', body: data.data.body || '' };
                  return next;
                });
              }
            }
          }
        } catch (err) {
          console.error('Failed to fetch custom templates:', err);
        }
      };
      fetchTemplates();
    }
  }, [isOpen]);

  const saveCustomTemplate = async () => {
    setTemplateSaving(true);
    setTemplateMessage('');
    const current = customTemplates[editingIndex] || { subject: '', body: '' };
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/userprofile/email-template`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ index: editingIndex, subject: current.subject, body: current.body })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setTemplateMessage('Template saved successfully!');
      } else {
        console.error('Save template error:', data);
        setTemplateMessage(data.error || data.message || 'Failed to save template.');
      }
    } catch (err) {
      console.error('Save template error:', err);
      setTemplateMessage('Failed to save template: ' + err.message);
    } finally {
      setTemplateSaving(false);
      setTimeout(() => setTemplateMessage(''), 3000);
    }
  };

  // Picking a template changes ONLY the subject + message. Re-validate just those
  // two fields and leave the rest of `errors` (To / CC / attachments) exactly as
  // it was. Previously these handlers ran the FULL validateForm() and replaced the
  // whole errors object — which re-checked `to` off the form value. The recipient
  // multiselect owns the To field, so a momentary mismatch there re-flagged a
  // perfectly valid recipient as "Email is required", disabling (freezing) the
  // Send button until the user re-ticked a contact. Only re-ticking cleared it
  // because toggleEmail() is the one place that deletes the `to` error.
  const revalidateSubjectMessage = (prevErrors, subject, message) => {
    const next = { ...prevErrors };
    const subjectError = validateSubject(subject);
    if (subjectError) next.subject = subjectError; else delete next.subject;
    const messageError = validateMessage(message);
    if (messageError) next.message = messageError; else delete next.message;
    return next;
  };

  const applyCustomTemplate = (index = editingIndex) => {
    const t = customTemplates[index];
    if (t && (t.subject !== '' || t.body !== '')) {
      const message = plainToHtml(t.body);
      setEmailForm(prev => ({ ...prev, subject: t.subject, message, selectedTemplate: -1 }));
      setErrors(prev => revalidateSubjectMessage(prev, t.subject, message));
      setActiveTab('compose');
    }
  };

  const updateEditingTemplate = (patch) => {
    setCustomTemplates(prev => {
      const next = [...prev];
      next[editingIndex] = { ...next[editingIndex], ...patch };
      return next;
    });
  };

  // Multiselect To helpers
  const toggleEmail = (email) => {
    setSelectedToEmails(prev => {
      const updated = prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email];
      const joined = updated.join(', ');
      setEmailForm(f => ({ ...f, to: joined }));
      setTouched(t => ({ ...t, to: true }));
      // validate
      if (updated.length === 0) {
        setErrors(e => ({ ...e, to: 'Email is required' }));
      } else {
        setErrors(e => { const n = { ...e }; delete n.to; return n; });
      }
      return updated;
    });
  };

  const selectAllEmails = () => {
    setSelectedToEmails([...emailOptions]);
    const joined = emailOptions.join(', ');
    setEmailForm(f => ({ ...f, to: joined }));
    setTouched(t => ({ ...t, to: true }));
    setErrors(e => { const n = { ...e }; delete n.to; return n; });
  };

  const deselectAllEmails = () => {
    setSelectedToEmails([]);
    setEmailForm(f => ({ ...f, to: '' }));
    setTouched(t => ({ ...t, to: true }));
    setErrors(e => ({ ...e, to: 'Email is required' }));
  };

  const removeSelectedEmail = (email) => {
    setSelectedToEmails(prev => {
      const updated = prev.filter(e => e !== email);
      const joined = updated.join(', ');
      setEmailForm(f => ({ ...f, to: joined }));
      if (updated.length === 0) {
        setErrors(e => ({ ...e, to: 'Email is required' }));
      }
      return updated;
    });
  };

  const handleFieldChange = (field, value) => {
    const updatedForm = { ...emailForm, [field]: value };
    setEmailForm(updatedForm);
    setTouched(prev => ({ ...prev, [field]: true }));
    let fieldError = '';
    switch (field) {
      case 'to': {
        const toEmails = value.split(',').map(e => e.trim()).filter(Boolean);
        if (toEmails.length === 0) { fieldError = 'Email is required'; }
        else {
          for (const email of toEmails) {
            const err = validateEmail(email);
            if (err) { fieldError = `Invalid email: ${email}`; break; }
          }
        }
        break;
      }
      case 'cc':
        if (value.trim()) {
          const ccEmails = value.split(',').map(email => email.trim());
          for (const email of ccEmails) {
            const ccError = validateEmail(email);
            if (ccError) { fieldError = `Invalid CC email: ${email}`; break; }
          }
        }
        break;
      case 'subject': fieldError = validateSubject(value); break;
      case 'message': fieldError = validateMessage(value); break;
    }
    if (fieldError) {
      setErrors(prev => ({ ...prev, [field]: fieldError }));
    } else {
      setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    }
    // Sync selectedToEmails when 'to' is manually typed
    if (field === 'to') {
      const parsed = value.split(',').map(e => e.trim()).filter(Boolean);
      setSelectedToEmails(parsed);
    }
  };

  const selectTemplate = (templateIndex) => {
    const tpl = emailTemplates[templateIndex];
    if (tpl) {
      const message = plainToHtml(tpl.message);
      // Same rule as applyCustomTemplate: a template only sets subject + message,
      // so don't touch the To/CC error state (which would freeze Send).
      setEmailForm(prev => ({ ...prev, selectedTemplate: templateIndex, subject: tpl.subject, message }));
      setErrors(prev => revalidateSubjectMessage(prev, tpl.subject, message));
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    const fileErrors = [];
    files.forEach(file => {
      const error = validateAttachment(file);
      if (error) fileErrors.push(`${file.name}: ${error}`);
      else validFiles.push(file);
    });
    if (fileErrors.length > 0) {
      setErrors(prev => ({ ...prev, attachments: fileErrors.join('; ') }));
    } else {
      setErrors(prev => { const n = { ...prev }; delete n.attachments; return n; });
    }
    if (validFiles.length > 0) {
      setEmailForm(prev => ({ ...prev, attachments: [...prev.attachments, ...validFiles] }));
    }
    event.target.value = '';
  };

  const removeAttachment = (index) => {
    setEmailForm(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== index) }));
    if (emailForm.attachments.length === 1) {
      setErrors(prev => { const n = { ...prev }; delete n.attachments; return n; });
    }
  };

  const handleSend = async () => {
    setIsValidating(true);
    setTouched({ to: true, cc: true, subject: true, message: true });
    const formErrors = validateForm();
    setErrors(formErrors);
    const isValid = Object.keys(formErrors).length === 0;
    if (!isValid) { setIsValidating(false); return; }
    try {
      if (onSend) {
        await onSend({ ...emailForm, sender_email_id: selectedSenderId }, recipient);
      } else {
        alert('Email sent successfully!');
      }
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      setErrors(prev => ({ ...prev, submit: 'Failed to send email. Please try again.' }));
    } finally {
      setIsValidating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(htmlToPlain(emailForm.message));
  };

  const isFormValid = Object.values(errors).every(v => !v);

  if (!isOpen) return null;

  const getRecipientName = () => {
    if (recipient.first_name && recipient.last_name) return `${recipient.first_name} ${recipient.last_name}`;
    if (recipient.name) return recipient.name;
    if (recipient.fullName) return recipient.fullName;
    if (recipient.email || recipient.value) return (recipient.email || recipient.value).split('@')[0];
    return 'Contact';
  };

  const recipientName = getRecipientName();
  const recipientEmail = recipient.email || recipient.value || recipient.emailAddress || '';
  const modalTitle = customTitle || `Send Email to ${recipientName}`;

  const getInputClasses = (field) => {
    const hasError = errors[field] && touched[field];
    const isSuccess = !errors[field] && touched[field] && emailForm[field];
    if (hasError) return 'border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-300 bg-red-50 dark:bg-red-900/20';
    if (isSuccess) return 'border-green-300 dark:border-green-600 focus:ring-green-500 focus:border-green-300 bg-green-50 dark:bg-green-900/20';
    return 'border-gray-200 dark:border-slate-600 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-300 dark:hover:border-slate-500';
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden border border-gray-200 dark:border-slate-700">

        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Mail className="w-5 h-5" />
                {modalTitle}
              </h2>
              {recipientEmail && (
                <p className="text-blue-100 text-sm mt-1">{recipientEmail}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-blue-100 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="space-y-4">

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-red-700 dark:text-red-400">{errors.submit}</span>
              </div>
            )}

            {/* Sender Email Verification Banner */}
            {senderStatus.status === 'no_sender_email' && (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">No Sender Email Configured</p>
                    <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                      You need to set up and verify a sender email before you can send emails.
                    </p>
                    <button
                      onClick={() => { onClose(); navigate('/email-settings'); }}
                      className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      Go to Email Settings
                    </button>
                  </div>
                </div>
              </div>
            )}

            {senderStatus.status === 'not_verified' && (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Sender Email Not Verified</p>
                    <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                      Your sender email <span className="font-medium">{senderStatus.senderEmail}</span> is not verified yet. Please check your inbox for the verification email or go to Email Settings to resend it.
                    </p>
                    <button
                      onClick={() => { onClose(); navigate('/email-settings'); }}
                      className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      Go to Email Settings
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Company name and postal address, required on the account before
                outreach can be sent. Lands the user on the tab that holds the
                missing field, in edit mode. */}
            <SenderIdentityNotice
              missing={identity.missing}
              blocking={identity.blocking}
              onGoToProfile={() => {
                onClose();
                navigate(`/profile?tab=${identity.missing[0]?.tab || 'company'}&edit=1`);
              }}
            />

            {/* Tabs: Compose / Custom Template */}
            <div className="flex border-b border-gray-200 dark:border-slate-700">
              <button
                onClick={() => setActiveTab('compose')}
                className={`px-4 py-2.5 text-sm font-medium transition-all border-b-2 ${
                  activeTab === 'compose'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                    : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300'
                }`}
              >
                <Mail className="w-4 h-4 inline mr-1.5" />
                Compose
              </button>
              <button
                onClick={() => setActiveTab('template')}
                className={`px-4 py-2.5 text-sm font-medium transition-all border-b-2 ${
                  activeTab === 'template'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                    : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-1.5" />
                Custom Template
              </button>
            </div>

            {/* Custom Template Tab */}
            {activeTab === 'template' && (() => {
              const current = customTemplates[editingIndex] || { subject: '', body: '' };
              const isEmpty = !current.subject && !current.body;
              return (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Save up to {TEMPLATE_SLOTS} reusable templates. Pick a slot below to edit it.
                  </p>
                </div>

                {/* Slot picker */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                    Template Slot
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {customTemplates.map((t, i) => {
                      const filled = !!(t.subject || t.body);
                      const active = i === editingIndex;
                      return (
                        <button
                          key={i}
                          onClick={() => setEditingIndex(i)}
                          title={t.subject || `Template ${i + 1}`}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
                            active
                              ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                              : filled
                                ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/50'
                                : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 border-gray-200 dark:border-slate-700 hover:bg-gray-200 dark:hover:bg-slate-700'
                          }`}
                        >
                          Template {i + 1}{filled ? '' : ' (empty)'}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                    Template Subject
                  </label>
                  <input
                    type="text"
                    value={current.subject}
                    onChange={(e) => updateEditingTemplate({ subject: e.target.value })}
                    placeholder="e.g., Partnership Opportunity - {{company}}"
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                    Template Body
                  </label>
                  <div className="quill-wrapper rounded-xl border border-gray-200 dark:border-slate-600">
                    <ReactQuill
                      key={`editor-${editingIndex}`}
                      theme="snow"
                      value={looksLikeHtml(current.body) ? current.body : plainToHtml(current.body)}
                      onChange={(html) => updateEditingTemplate({ body: html })}
                      modules={QUILL_MODULES}
                      formats={QUILL_FORMATS}
                      placeholder="Write your reusable email template here..."
                    />
                  </div>
                </div>

                {templateMessage && (
                  <div className={`flex items-center gap-1.5 text-sm ${templateMessage.includes('success') ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                    {templateMessage.includes('success') ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    <span>{templateMessage}</span>
                  </div>
                )}

                <div className="flex gap-2.5">
                  <button
                    onClick={saveCustomTemplate}
                    disabled={templateSaving || isEmpty}
                    className={`px-4 py-2.5 rounded-xl font-medium text-sm flex items-center gap-2 transition-all ${
                      templateSaving || isEmpty
                        ? 'bg-gray-300 dark:bg-slate-700 text-gray-500 dark:text-slate-500 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    <Save className="w-4 h-4" />
                    {templateSaving ? 'Saving...' : `Save Template ${editingIndex + 1}`}
                  </button>
                  <button
                    onClick={() => applyCustomTemplate(editingIndex)}
                    disabled={isEmpty}
                    className={`px-4 py-2.5 rounded-xl font-medium text-sm flex items-center gap-2 transition-all ${
                      isEmpty
                        ? 'bg-gray-300 dark:bg-slate-700 text-gray-500 dark:text-slate-500 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    <Mail className="w-4 h-4" />
                    Use This Template
                  </button>
                </div>
              </div>
              );
            })()}

            {/* Compose Tab */}
            {activeTab === 'compose' && <>

            {/* From Field — sender selection */}
            {senderEmails.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                  From <span className="text-gray-400 dark:text-slate-500 font-normal">(select sender)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 text-gray-400 dark:text-slate-500" />
                  </div>
                  <select
                    value={selectedSenderId}
                    onChange={(e) => setSelectedSenderId(e.target.value)}
                    className="w-full pl-10 pr-8 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all dark:bg-slate-800 dark:text-slate-100 appearance-none cursor-pointer"
                  >
                    {senderEmails.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.email}{e.is_primary ? ' (Primary)' : ''}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400 dark:text-slate-500" />
                  </div>
                </div>
              </div>
            )}

            {/* To Field - Multiselect */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                To <span className="text-red-500">*</span>
                {emailOptions.length > 1 && (
                  <span className="text-gray-400 dark:text-slate-500 font-normal ml-2">({emailOptions.length} contacts found)</span>
                )}
              </label>

              {/* Selection-only: checkbox list — typing into the To field is disabled
                  on purpose. The user picks from the contacts surfaced by the finder. */}
              {emailOptions.length >= 1 ? (
                <div className={`border rounded-xl overflow-hidden transition-all ${
                  errors.to && touched.to ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-slate-600'
                }`}>
                  {emailOptions.length > 1 && (
                    <label className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer border-b border-gray-100 dark:border-slate-700 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors ${
                      emailOptions.every(e => selectedToEmails.includes(e)) ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-50 dark:bg-slate-800/50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={emailOptions.every(e => selectedToEmails.includes(e))}
                        onChange={(e) => e.target.checked ? selectAllEmails() : deselectAllEmails()}
                        className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm font-medium text-green-700 dark:text-green-400">
                        Select all ({emailOptions.length})
                      </span>
                    </label>
                  )}
                  {emailOptions.map((email, idx) => {
                    const isSelected = selectedToEmails.includes(email);
                    return (
                      <label
                        key={idx}
                        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors ${
                          isSelected ? 'bg-blue-50 dark:bg-slate-700/50' : 'dark:bg-slate-800'
                        } ${idx < emailOptions.length - 1 ? 'border-b border-gray-100 dark:border-slate-700' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleEmail(email)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className={`text-sm ${isSelected ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-700 dark:text-slate-300'}`}>
                          {email}
                        </span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="w-full px-4 py-2.5 border rounded-xl bg-gray-50 dark:bg-slate-800 text-sm text-gray-500 dark:text-slate-400 border-gray-200 dark:border-slate-600">
                  No contacts found for this affiliate.
                </div>
              )}

              {errors.to && touched.to && (
                <div className="flex items-center gap-1.5 text-red-500 dark:text-red-400 text-sm mt-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />
                  <span>{errors.to}</span>
                </div>
              )}
              {!errors.to && selectedToEmails.length > 0 && (
                <div className="flex items-center gap-1.5 text-green-500 dark:text-green-400 text-sm mt-1.5">
                  <CheckCircle className="w-3.5 h-3.5" />
                  <span>{selectedToEmails.length} recipient{selectedToEmails.length > 1 ? 's' : ''} selected</span>
                </div>
              )}
            </div>

            {/* Social Media Links */}
            {socialLinks.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                  Social Media
                </label>
                <div className="flex flex-wrap gap-2">
                  {socialLinks.map((link, idx) => (
                    <a
                      key={idx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 ${link.color} text-white text-xs font-medium rounded-lg hover:opacity-90 transition-opacity`}
                    >
                      <ExternalLink className="w-3 h-3" />
                      {link.platform}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* CC Field */}
            {allowCc && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                  CC <span className="text-gray-400 dark:text-slate-500 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={emailForm.cc}
                  onChange={(e) => handleFieldChange('cc', e.target.value)}
                  onBlur={() => setTouched(prev => ({ ...prev, cc: true }))}
                  placeholder="cc@example.com (separate multiple with commas)"
                  className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 transition-all dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500 ${getInputClasses('cc')}`}
                />
                {errors.cc && touched.cc && (
                  <div className="flex items-center gap-1.5 text-red-500 dark:text-red-400 text-sm mt-1.5">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>{errors.cc}</span>
                  </div>
                )}
              </div>
            )}

            {/* Template Selection */}
            {showTemplateSelection && emailTemplates.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Email Template</label>
                <div className="flex gap-2 flex-wrap">
                  {customTemplates.map((t, i) => (
                    (t.subject || t.body) ? (
                      <button
                        key={`my-${i}`}
                        onClick={() => applyCustomTemplate(i)}
                        title={t.subject || `My Template ${i + 1}`}
                        className={`px-4 py-2 rounded-xl font-medium text-sm transition-all bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50 border border-green-200 dark:border-green-700`}
                      >
                        <FileText className="w-3.5 h-3.5 inline mr-1" />
                        My Template {i + 1}
                      </button>
                    ) : null
                  ))}
                  {emailTemplates.map((template, index) => (
                    <button
                      key={index}
                      onClick={() => selectTemplate(index)}
                      className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                        emailForm.selectedTemplate === index
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700 border border-gray-200 dark:border-slate-700'
                      }`}
                    >
                      {template.name || `Template ${index + 1}`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Subject Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Subject {validationOptions.requireSubject && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={emailForm.subject}
                onChange={(e) => handleFieldChange('subject', e.target.value)}
                onBlur={() => setTouched(prev => ({ ...prev, subject: true }))}
                placeholder="Email subject"
                className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 transition-all dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500 ${getInputClasses('subject')}`}
              />
              <div className="flex justify-between items-start mt-1.5">
                {errors.subject && touched.subject ? (
                  <div className="flex items-center gap-1.5 text-red-500 dark:text-red-400 text-sm">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>{errors.subject}</span>
                  </div>
                ) : !errors.subject && touched.subject && emailForm.subject ? (
                  <div className="flex items-center gap-1.5 text-green-500 dark:text-green-400 text-sm">
                    <CheckCircle className="w-3.5 h-3.5" />
                    <span>Valid</span>
                  </div>
                ) : <div />}
                <div className="text-xs text-gray-400 dark:text-slate-500">
                  {emailForm.subject.length}/{validationOptions.maxSubjectLength}
                </div>
              </div>
            </div>

            {/* Message Field (rich text) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Message {validationOptions.requireMessage && <span className="text-red-500">*</span>}
              </label>
              <div className={`quill-wrapper rounded-xl border transition-all ${
                errors.message && touched.message
                  ? 'border-red-300 dark:border-red-600'
                  : (!errors.message && touched.message && htmlToPlain(emailForm.message).trim())
                    ? 'border-green-300 dark:border-green-600'
                    : 'border-gray-200 dark:border-slate-600'
              }`}>
                <ReactQuill
                  theme="snow"
                  value={emailForm.message}
                  onChange={(html) => handleFieldChange('message', html)}
                  onBlur={() => setTouched(prev => ({ ...prev, message: true }))}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  placeholder="Write your message..."
                />
              </div>
              <div className="flex justify-between items-start mt-1.5">
                {errors.message && touched.message ? (
                  <div className="flex items-center gap-1.5 text-red-500 dark:text-red-400 text-sm">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>{errors.message}</span>
                  </div>
                ) : !errors.message && touched.message && htmlToPlain(emailForm.message).trim() ? (
                  <div className="flex items-center gap-1.5 text-green-500 dark:text-green-400 text-sm">
                    <CheckCircle className="w-3.5 h-3.5" />
                    <span>Valid</span>
                  </div>
                ) : <div />}
                <div className="text-xs text-gray-400 dark:text-slate-500">
                  {htmlToPlain(emailForm.message).length}/{validationOptions.maxMessageLength}
                </div>
              </div>
            </div>

            {/* Attachments */}
            {allowAttachments && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Attachments</label>
                <div className="flex items-center gap-4">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      multiple
                      className="hidden"
                      accept={validationOptions.allowedFileTypes.join(',')}
                    />
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl hover:bg-gray-200 dark:hover:bg-slate-700 transition-all cursor-pointer">
                      <Paperclip className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-slate-300">Add Attachment</span>
                    </div>
                  </label>
                  <div className="text-xs text-gray-400 dark:text-slate-500">
                    Max {(validationOptions.maxAttachmentSize / 1024 / 1024).toFixed(0)}MB per file
                  </div>
                </div>
                {errors.attachments && (
                  <div className="flex items-center gap-1.5 text-red-500 dark:text-red-400 text-sm mt-1.5">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>{errors.attachments}</span>
                  </div>
                )}
                {emailForm.attachments.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {emailForm.attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded-lg border border-gray-100 dark:border-slate-700">
                        <div className="flex items-center gap-2">
                          <Paperclip className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500" />
                          <span className="text-sm text-gray-700 dark:text-slate-300">{file.name}</span>
                          <span className="text-xs text-gray-400 dark:text-slate-500">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <button
                          onClick={() => removeAttachment(index)}
                          className="text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-400 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-800 rounded-xl hover:bg-gray-200 dark:hover:bg-slate-700 transition-all font-medium text-sm"
          >
            Cancel
          </button>
          <div className="flex gap-2.5">
            <button
              onClick={copyToClipboard}
              className="px-5 py-2.5 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-all font-medium text-sm flex items-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Copy
            </button>
            <button
              onClick={handleSend}
              // identity.blocking: the server would refuse this send, so the
              // button says so up front instead of failing after the fact.
              disabled={!isFormValid || isValidating || !senderStatus.verified || identity.blocking}
              className={`px-6 py-2.5 rounded-xl transition-all font-semibold text-sm flex items-center gap-2 ${
                !isFormValid || isValidating || !senderStatus.verified || identity.blocking
                  ? 'bg-gray-300 dark:bg-slate-700 text-gray-500 dark:text-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-sm hover:shadow-md'
              }`}
            >
              <Send className="w-4 h-4" />
              {isValidating ? 'Sending...' : 'Send Email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPopup;
